import React, { useState, useEffect } from "react";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import SearchForm from "../components/Foreclosure/SearchForm";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FaFilePdf, FaPrint } from "react-icons/fa";

const Noc = () => {
  const { adminUser } = useAdminAuth();
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [searchType, setSearchType] = useState("prospectId");
  const [searchValue, setSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [branchName, setBranchName] = useState("");

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage("");
      setMemberData(null);
      setBranchName("");

      const response = await axios.get(`${BASE_URL}/api/getForeclosureDetails`, {
        params: {
          searchType,
          searchValue,
        },
      });

      if (response.data.success) {
        setMemberData(response.data.member);
        setBranchName(response.data.branchName);
      } else {
        setErrorMessage(
          response.data.error || "Failed to fetch customer details"
        );
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
      setErrorMessage(
        error.response?.data?.error ||
        "An error occurred while fetching customer details"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-section, .print-section * {
          visibility: visible;
        }
        .print-section {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        @page {
          size: portrait;
          margin: 10mm;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleDownloadPdf = (event) => {
    event.preventDefault();
    const printSection = document.querySelector(".noc-certificate");
    if (!printSection) return;
    html2canvas(printSection, {
      scale: 2,
      useCORS: true,
      logging: false,
      letterRendering: true,
    }).then((canvas) => {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = pdf.internal.pageSize.getWidth() - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(
        canvas.toDataURL("image/jpeg", 1.0),
        "JPEG",
        10,
        10,
        imgWidth,
        imgHeight
      );
      pdf.save(`NOC_${memberData?.loanId || "document"}.pdf`);
    });
  };

  const handlePrint = (event) => {
    event.preventDefault();
    window.print();
  };

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          {/* Top Header & Search Bar */}
          <div className="master-search-section inline-header">
            <h1 className="TableHeaderText m-0">NOC Management</h1>

            {adminUser.userType === "Accounts Manager" && (
              <SearchForm
                searchType={searchType}
                searchValue={searchValue}
                handleSearchTypeChange={handleSearchTypeChange}
                handleSearchValueChange={handleSearchValueChange}
                handleSearch={handleSearch}
              />
            )}
          </div>

          {/* List/Content Section */}
          <div
            className="master-list-section"
            style={{ padding: "20px", overflowY: "auto" }}
          >
            {isLoading && (
              <div className="text-center my-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="alert alert-danger mb-3" role="alert">
                {errorMessage}
              </div>
            )}

            {memberData ? (
              <div className="mx-auto" style={{ maxWidth: "850px", width: "100%" }}>
                <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
                  <button
                    className="btn btn-addCredential m-0"
                    onClick={handleDownloadPdf}
                  >
                    <FaFilePdf className="me-2" />
                    Download PDF
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={handlePrint}
                  >
                    <FaPrint className="me-2" />
                    Print NOC
                  </button>
                </div>

                <div className="noc-certificate p-4 border print-section bg-white text-dark shadow-sm rounded-3">
                  <h3 className="text-center mb-4 fw-bold" style={{ color: "#0f172a" }}>
                    NO DUES CERTIFICATE
                  </h3>

                  <div className="text-end mb-3 text-muted">
                    Date: {new Date().toLocaleDateString("en-US")}
                  </div>

                  <p className="text-start fw-semibold">
                    To, Mr/Ms/Mrs. {memberData.memberName}
                  </p>

                  <p className="mb-4 text-start text-muted">
                    Address: {memberData.permanentDoorNo},{" "}
                    {memberData.permanentStreetNo},{" "}
                    {memberData.permanentPanchayat},{" "}
                    {memberData.permanentTaluk},{" "}
                    {memberData.permanentDistrict}-
                    {memberData.permanentPincode}.
                  </p>

                  <p className="mb-4 text-start">
                    Dear Sir/Madam, we confirm that the below loan account with
                    Vetrivikas Capital Stands adjusted in full and final. All
                    dues towards the entire loan amount payable against below
                    mentioned loan account are received and there is no
                    outstanding amount.
                  </p>

                  <table className="table table-bordered mb-4">
                    <tbody>
                      <tr>
                        <td className="fw-semibold" style={{ width: "35%" }}>Product</td>
                        <td>{memberData.loanType}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold">Loan Ac no</td>
                        <td>{memberData.loanId}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold">Disbursement Date</td>
                        <td>
                          {new Date(
                            memberData.branchManagerStatusUpdatedAt
                          ).toLocaleDateString("en-US")}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold">Loan Amount</td>
                        <td>
                          ₹{memberData.sanctionedLoanAmountBySanctionCommittee}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold">Date of Loan Closure</td>
                        <td>
                          {new Date(
                            memberData.loanCloseDate
                          ).toLocaleDateString("en-US")}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <ul className="list-unstyled p-3 text-start small text-muted border-top">
                    <li className="mb-2">
                      • Vetrivikas Capital never sends requests for your internet
                      banking username, password, debit or credit card numbers,
                      bank account numbers or any other sensitive information by
                      email. If you ever receive something of the sort, which
                      appears to be from the bank, please do not respond. Instead,
                      forward the mail to office@vetrivikascapital.com so we can
                      address the issue.
                    </li>
                    <li className="mb-2">
                      • This statement is a system generated output and requires no
                      signature.
                    </li>
                    <li>
                      • Contact us: Call us at 04524909438 or email us at
                      office@vetrivikascapital.com
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              !isLoading && (
                <div className="text-center py-5 text-muted">
                  {adminUser.userType === "Accounts Manager"
                    ? "Enter search criteria to view member's No Dues Certificate."
                    : "Only Accounts Manager can search and generate NOC certificates."}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Noc;
